import { TFunction } from 'i18next';
import moment from 'moment';
import { append, has, isNil, prop } from 'ramda';
import { StatsInterval } from 'src/js/model/api/request/stream-stat-request';
import { StreamSrtStatsResponse } from 'src/js/model/api/response/stream-srt-stats-response';
import { StatisticSectionViewModel } from 'src/js/pages/statistic/statistic-view-model';
import { constant } from 'src/js/constant';
import { ChartDataViewModel, ChartViewModel } from 'src/js/view-model/chart-view-model';

// remove undefined rows
export const filterUndefinedRows = (sections: StatisticSectionViewModel[]): StatisticSectionViewModel[] => {
  sections.forEach((section) => {
    const twoColumns = section.twoColumns;
    section.rows = section.rows.filter((row) => {
      if (twoColumns === true) {
        return !isNil(row.value) || !isNil(row.value2); // TODO change to != null check
      }
      return !isNil(row.value);
    });
  });

  // DEV check unit keys (readonly check)
  /*
  const sectionskeys:any = {};
  const keys:any = {};
  sections.forEach((section) => {
    if (sectionskeys[section.title] !== undefined) {
      console.error('duplicated sections key', section.title);
    }
    sectionskeys[section.title] = 1;
    section.rows.map((row) => {
      if (keys[section.title + '-' + row.name] !== undefined) {
        console.error('duplicated key', section.title + '-' + row.name);
      }
      keys[section.title + '-' + row.name] = 1;
    });
  });*/

  return sections;
};

export const mapStreamSRTStatisticToPlotData = (
  data: StreamSrtStatsResponse[],
  yFields: string[],
  t: TFunction,
  showRedundancy?: boolean,
  csvValuesSection = 'csvValues',
): ChartViewModel[] => {
  // Create the view models with the proper key (will translate later)
  let viewModels: ChartViewModel[] = yFields.map((yField: string) => ({
    name: yField,
    values: [],
    label: yField,
  }));
  if (showRedundancy) {
    viewModels = viewModels.concat(
      yFields.map((yField: string) => ({
        name: yField + '_Path2',
        values: [],
        label: yField + '_Path2',
      })),
    );
  }

  // Map all the data to the view models
  data.slice(-120).map((series: StreamSrtStatsResponse) => {
    // slice() is a failover if the backend returns spurious large amount of CSV data
    viewModels.map((field: ChartViewModel) => {
      // @ts-ignore
      const value = has(field.name, series) && prop(field.name, series);
      if (!isNil(value)) {
        field.values = append({ collectedAt: moment(series.DateTime), value: Number(value) }, field.values);
      }
    });
  });
  // Translate the keys
  const mapped = viewModels.map((viewModel) => ({
    name: viewModel.name,
    label: t(`statistics.chart.${csvValuesSection}.${viewModel.name}`),
    values: viewModel.values,
  }));
  return mapped;
};

/*
 * Victory helper
 */
export interface VictoryData {
  x: string | number; // number == unix timestamp
  y: number;
  yOriginal?: number; // because y number can be manipulated when y axis range changes (kbps => Mbps). Original value is kept here, can be used by tooltip
}

export const mapToVictoryData = (
  viewModels: ChartDataViewModel[],
  timescale: StatsInterval,
  lineName?: string,
): VictoryData[] => {
  return viewModels.map((viewModel, _index) => ({
    x: viewModel.collectedAt.format(
      timescale === StatsInterval.LAST_5_MINS ? constant.momentFormat.timeWithSeconds : constant.momentFormat.time,
    ),
    y: viewModel.value,
    lineName: lineName /* this is repeated but needed for tooltips callback */,
  }));
};

export const mapToVictoryInMinutes = (viewModels: ChartDataViewModel[], lineName?: string): VictoryData[] => {
  if (viewModels === undefined || viewModels.length == 0) {
    return viewModels as any;
  }
  return viewModels.map((viewModel, index) => ({
    // next release just do this NKE-3114
    // x: viewModel.collectedAt.unix(),

    x: ((60 - index) / 12).toString() + 'm',

    y: viewModel.value,
    lineName: lineName /* this is repeated but needed for tooltips callback */,
  }));
};
